'use client';

import {
  PointerEvent as ReactPointerEvent,
  useCallback,
  useRef,
  useState,
} from 'react';

const MIN_THUMB_WIDTH_PX = 32;

/**
 * Drives a horizontal scrollbar rendered above a scroll container. The thumb is
 * a plain element rather than a native scrollbar so it stays visible on every
 * platform, which is the whole point of lifting the bar above the content.
 */
export const useSyncedHorizontalScrollbar = <
  TScroller extends HTMLElement = HTMLDivElement,
  TTrack extends HTMLElement = HTMLDivElement,
  TThumb extends HTMLElement = HTMLDivElement,
>() => {
  const scrollerNode = useRef<TScroller | null>(null);
  const trackNode = useRef<TTrack | null>(null);
  const thumbNode = useRef<TThumb | null>(null);
  const detachScroller = useRef<(() => void) | null>(null);

  const [hasOverflow, setHasOverflow] = useState(false);

  const positionThumb = useCallback(() => {
    const scroller = scrollerNode.current;
    const track = trackNode.current;
    const thumb = thumbNode.current;

    if (!scroller || !track || !thumb) return;

    const scrollableWidth = scroller.scrollWidth - scroller.clientWidth;

    if (scrollableWidth <= 0) return;

    const thumbWidth = Math.max(
      MIN_THUMB_WIDTH_PX,
      (scroller.clientWidth / scroller.scrollWidth) * track.clientWidth,
    );

    const travel = track.clientWidth - thumbWidth;

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${
      (scroller.scrollLeft / scrollableWidth) * travel
    }px)`;
  }, []);

  // Kept out of the scroll listener: it is the only step that touches React
  // state, and re-rendering the table on every scroll frame would be wasteful.
  const measure = useCallback(() => {
    const scroller = scrollerNode.current;

    if (!scroller) return;

    // 1px tolerance: sub-pixel widths never match exactly.
    setHasOverflow(scroller.scrollWidth - scroller.clientWidth > 1);
    positionThumb();
  }, [positionThumb]);

  const scrollerRef = useCallback(
    (node: TScroller | null) => {
      detachScroller.current?.();
      detachScroller.current = null;
      scrollerNode.current = node;

      if (!node) return;

      const observer = new ResizeObserver(measure);

      observer.observe(node);
      // The children matter too: the container keeps its width while the
      // content grows, so `scrollWidth` changes without it resizing.
      Array.from(node.children).forEach((child) => observer.observe(child));

      node.addEventListener('scroll', positionThumb, { passive: true });

      detachScroller.current = () => {
        observer.disconnect();
        node.removeEventListener('scroll', positionThumb);
      };

      measure();
    },
    [measure, positionThumb],
  );

  // The track and the thumb only mount once there is overflow, so they lay
  // themselves out as they arrive.
  const trackRef = useCallback(
    (node: TTrack | null) => {
      trackNode.current = node;
      positionThumb();
    },
    [positionThumb],
  );

  const thumbRef = useCallback(
    (node: TThumb | null) => {
      thumbNode.current = node;
      positionThumb();
    },
    [positionThumb],
  );

  const onThumbPointerDown = useCallback((event: ReactPointerEvent<TThumb>) => {
    const scroller = scrollerNode.current;
    const track = trackNode.current;
    const thumb = thumbNode.current;

    if (!scroller || !track || !thumb) return;

    const travel = track.clientWidth - thumb.clientWidth;

    if (travel <= 0) return;

    event.preventDefault();

    const startClientX = event.clientX;
    const startScrollLeft = scroller.scrollLeft;
    const scrollableWidth = scroller.scrollWidth - scroller.clientWidth;

    const onPointerMove = (moveEvent: PointerEvent) => {
      scroller.scrollLeft =
        startScrollLeft +
        ((moveEvent.clientX - startClientX) / travel) * scrollableWidth;
    };

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  }, []);

  const onTrackPointerDown = useCallback((event: ReactPointerEvent<TTrack>) => {
    const scroller = scrollerNode.current;
    const track = trackNode.current;
    const thumb = thumbNode.current;

    if (!scroller || !track || !thumb || event.target === thumb) return;

    const travel = track.clientWidth - thumb.clientWidth;

    if (travel <= 0) return;

    const centeredThumbLeft =
      event.clientX -
      track.getBoundingClientRect().left -
      thumb.clientWidth / 2;

    const progress = Math.min(Math.max(centeredThumbLeft / travel, 0), 1);

    scroller.scrollLeft =
      progress * (scroller.scrollWidth - scroller.clientWidth);
  }, []);

  return {
    scrollerRef,
    trackRef,
    thumbRef,
    hasOverflow,
    onThumbPointerDown,
    onTrackPointerDown,
  };
};
